/* Sniggles cart — drawer controller + /cart page enhancer. Vanilla JS, no
   framework. Honors the PDP integration contract:
     - exposes window.SnigglesCart = { open, close, refresh, isOpen }
     - listens for document 'sniggles:cart:added' -> refresh() then open()
     - header bag icon [data-sn-cart-open] click opens the drawer (href="/cart"
       stays as the no-JS fallback)
   Cart state lives in Shopify's AJAX Cart API (/cart/change.js, /cart/update.js).
   The drawer + page re-render through the Section Rendering API, swapping the
   inner HTML of [data-sn-cart-render] so quantities / subtotal / progress stay
   correct without a full reload. Service lines (_service_of / _parent_line) are
   removed together with their parent line. */
(function () {
  'use strict';

  var roots = [];      // { el, sectionId, render }
  var drawer = null;   // the root that owns the slide-in panel
  var lastFocus = null;
  var busy = false;

  // ---- helpers -----------------------------------------------------------
  function sectionUrl(sectionId) {
    var path = window.location.pathname || '/';
    return path + '?sections=' + encodeURIComponent(sectionId);
  }

  function allSectionIds() {
    return roots.map(function (r) { return r.sectionId; }).join(',');
  }

  function updateCounts(count) {
    var n = parseInt(count, 10);
    if (isNaN(n)) n = 0;
    // Header bag badge(s)
    document.querySelectorAll('.sn-cart-count').forEach(function (el) {
      el.textContent = n > 0 ? String(n) : '';
    });
    // Header bag link a11y count
    document.querySelectorAll('[data-sn-cart-open]').forEach(function (el) {
      el.setAttribute('data-cart-count', String(n));
    });
  }

  // Swap the rendered region of a root from a full section HTML string.
  function applyHTML(root, html) {
    if (!root || !html) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('[data-sn-cart-render]');
    if (!fresh || !root.render) return;
    root.render.innerHTML = fresh.innerHTML;
    if (fresh.getAttribute('data-cart-count') != null) {
      root.render.setAttribute('data-cart-count', fresh.getAttribute('data-cart-count'));
      updateCounts(fresh.getAttribute('data-cart-count'));
    }
  }

  // GET-refresh a single root via the Section Rendering API.
  function refreshRoot(root) {
    return fetch(sectionUrl(root.sectionId), { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var html = data && data[root.sectionId];
        if (html) applyHTML(root, html);
      })
      .catch(function () { /* leave current DOM in place on failure */ });
  }

  function refreshAll() {
    return Promise.all(roots.map(refreshRoot));
  }

  // Apply a mutation response that may carry rendered sections; fall back to
  // a per-root GET refresh for any section the response did not include.
  function applyMutation(data) {
    var sections = (data && data.sections) || {};
    return Promise.all(roots.map(function (root) {
      if (sections[root.sectionId]) {
        applyHTML(root, sections[root.sectionId]);
        return Promise.resolve();
      }
      return refreshRoot(root);
    }));
  }

  function mutate(url, body) {
    if (busy) return Promise.resolve();
    busy = true;
    roots.forEach(function (r) { r.el.classList.add('is-busy'); });
    body.sections = allSectionIds();
    body.sections_url = window.location.pathname;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (res) { return res.json(); })
      .then(function (data) { return applyMutation(data); })
      .catch(function () { return refreshAll(); })
      .finally(function () {
        busy = false;
        roots.forEach(function (r) { r.el.classList.remove('is-busy'); });
      });
  }

  // ---- cart operations ---------------------------------------------------
  function changeQty(key, quantity) {
    return mutate('/cart/change.js', { id: key, quantity: quantity });
  }

  // Remove a line; if it is a parent (not a service) also remove its child
  // service lines (whose data-parent === this line's variant id).
  function removeLine(root, lineEl) {
    var updates = {};
    var key = lineEl.getAttribute('data-line-key');
    updates[key] = 0;
    if (!lineEl.hasAttribute('data-service')) {
      var vid = lineEl.getAttribute('data-variant-id');
      if (vid) {
        root.el.querySelectorAll('[data-sn-line][data-parent]').forEach(function (child) {
          if (child.getAttribute('data-parent') === vid) {
            var ck = child.getAttribute('data-line-key');
            if (ck) updates[ck] = 0;
          }
        });
      }
    }
    return mutate('/cart/update.js', { updates: updates });
  }

  // ---- drawer open / close ----------------------------------------------
  function focusables(panel) {
    return Array.prototype.slice.call(panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) { return el.offsetParent !== null; });
  }

  function trap(e) {
    if (!drawer || e.key !== 'Tab') return;
    var panel = drawer.el.querySelector('[data-sn-cart-panel]');
    if (!panel) return;
    var list = focusables(panel);
    if (!list.length) return;
    var first = list[0];
    var last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape' && isOpen()) { close(); return; }
    trap(e);
  }

  function isOpen() {
    return !!(drawer && drawer.el.classList.contains('is-open'));
  }

  function open() {
    if (!drawer) { window.location.href = '/cart'; return; }
    if (isOpen()) return;
    var scrim = drawer.el.querySelector('[data-sn-cart-scrim]');
    var panel = drawer.el.querySelector('[data-sn-cart-panel]');
    lastFocus = document.activeElement;
    if (scrim) scrim.hidden = false;
    if (panel) panel.hidden = false;
    drawer.el.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(function () {
      drawer.el.classList.add('is-open');
    });
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeydown);
    if (panel) {
      requestAnimationFrame(function () {
        var closeBtn = panel.querySelector('[data-sn-cart-close]');
        (closeBtn || panel).focus();
      });
    }
  }

  function close() {
    if (!drawer || !isOpen()) return;
    var scrim = drawer.el.querySelector('[data-sn-cart-scrim]');
    var panel = drawer.el.querySelector('[data-sn-cart-panel]');
    drawer.el.classList.remove('is-open');
    drawer.el.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', onKeydown);
    window.setTimeout(function () {
      if (scrim) scrim.hidden = true;
      if (panel) panel.hidden = true;
    }, 400);
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  }

  function refresh() {
    return refreshAll();
  }

  // ---- event delegation --------------------------------------------------
  // NB: bindRoot is called with the DOM element. The owning root object is
  // resolved from `roots` at CLICK time, so it stays correct across re-renders
  // and — critically — a mismatched argument can never throw here during
  // collect(). (Previously bindRoot expected a root object and dereferenced
  // root.el on a DOM element, throwing a TypeError that aborted collect() before
  // `drawer` was assigned and aborted boot() before the bag icon was bound —
  // which is why clicking the bag fell through to /cart instead of opening.)
  function bindRoot(el) {
    el.addEventListener('click', function (e) {
      var root = null;
      for (var i = 0; i < roots.length; i++) {
        if (roots[i].el === el) { root = roots[i]; break; }
      }
      if (!root) return;

      var closeEl = e.target.closest('[data-sn-cart-close]');
      if (closeEl && root === drawer) {
        // Let real navigation links (empty-state) proceed; plain buttons close.
        if (closeEl.tagName !== 'A') { e.preventDefault(); }
        close();
        return;
      }
      var scrim = e.target.closest('[data-sn-cart-scrim]');
      if (scrim) { close(); return; }

      var line = e.target.closest('[data-sn-line]');
      if (!line) return;
      var key = line.getAttribute('data-line-key');

      if (e.target.closest('[data-sn-remove]')) {
        e.preventDefault();
        removeLine(root, line);
        return;
      }
      var val = line.querySelector('[data-sn-qty-val]');
      var current = val ? parseInt(val.textContent, 10) || 1 : 1;
      if (e.target.closest('[data-sn-qty-inc]')) {
        e.preventDefault();
        changeQty(key, current + 1);
      } else if (e.target.closest('[data-sn-qty-dec]')) {
        e.preventDefault();
        if (current > 1) changeQty(key, current - 1);
        else removeLine(root, line);
      }
    });
  }

  // ---- boot --------------------------------------------------------------
  function collect() {
    roots = [];
    drawer = null;
    document.querySelectorAll('[data-render-section]').forEach(function (el) {
      if (el.dataset.snCartBound === '1') {
        // already bound; keep reference
      } else {
        el.dataset.snCartBound = '1';
        bindRoot(el);
      }
      var root = {
        el: el,
        sectionId: el.getAttribute('data-render-section'),
        render: el.querySelector('[data-sn-cart-render]')
      };
      roots.push(root);
      if (el.hasAttribute('data-sn-cart') || el.querySelector('[data-sn-cart-panel]')) {
        drawer = root;
      }
    });
  }

  function boot() {
    collect();

    // Header bag icon(s) -> open drawer (progressive enhancement).
    document.querySelectorAll('[data-sn-cart-open]').forEach(function (el) {
      if (el.dataset.snCartOpenBound === '1') return;
      el.dataset.snCartOpenBound = '1';
      el.addEventListener('click', function (e) {
        if (!drawer) return; // no drawer on this page: follow href to /cart
        e.preventDefault();
        open();
      });
    });

    // PDP add-to-bag contract.
    if (!window.__snCartAddedBound) {
      window.__snCartAddedBound = true;
      document.addEventListener('sniggles:cart:added', function () {
        refresh().then(open);
      });
    }
  }

  window.SnigglesCart = {
    open: open,
    close: close,
    refresh: refresh,
    isOpen: isOpen
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Re-collect when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', boot);
})();
